import { extractTextFromHTML, formatDate, templateString } from '@/utils/helpers';
import {
  Page,
  Document,
  View,
  Svg,
  Path,
  Text,
  Defs,
  LinearGradient,
  Stop,
  Image,
  Styles,
  Link
} from '@react-pdf/renderer';
import { FC, useCallback, useMemo } from 'react';
import { getFullImageUrl, getThumbnailImageUrl } from '../../SearchResults/Snippets/helpers';
import { GeneratedCreditPlanProps } from '../Panels/GeneratedCreditPlanSidePanel';
import { IconMessageBlock, Pdf } from '@/@types/content';
import { CreditPlanProductGroup, CreditPlanResultItem, IKeywordItem } from '@/@types/client-api';
import type { RichTextBlock as RichTextBlockProps } from '@/@types/content';

type CreditPlanPdfProps = Pick<GeneratedCreditPlanProps, 'aiPoweredText' | 'jurisdictionName'> & {
  styles: Styles;
  dateDisplay: string;
  content?: Pdf;
  betaIcon?: boolean;
  total: number;
  live?: CreditPlanProductGroup;
  onDemand?: CreditPlanProductGroup;
  fileName: string;
  pmMessage?: IconMessageBlock;
  disclaimer?: RichTextBlockProps;
  openJurisdictionBrowseUrl?: string;
};

const CreditPlanPdf: FC<CreditPlanPdfProps> = ({
  styles,
  dateDisplay,
  aiPoweredText,
  betaIcon,
  jurisdictionName,
  total,
  live,
  onDemand,
  fileName,
  pmMessage,
  disclaimer,
  openJurisdictionBrowseUrl,
  content: { heading, subheading } = {}
}) => {
  const creditDetailsFormattedToString = useCallback((creditDetailsFormatted: IKeywordItem[]) => {
    if (!creditDetailsFormatted || creditDetailsFormatted.length === 0) return '';

    const firstCreditDetail = creditDetailsFormatted[0];
    return firstCreditDetail.keyword || undefined;
  }, []);

  const formatDotSeparatedContent = useCallback((items: (string | null | undefined)[]) => {
    return items?.filter(a => !!a)?.join('  •  ');
  }, []);

  const getLiveProgramDateParts = useCallback((item: CreditPlanResultItem) => {
    const date =
      item.startTimeUtc != null
        ? new Date(item.startTimeUtc)
        : item.locations?.[0]?.startTimeUtc != null
          ? new Date(item.locations[0].startTimeUtc)
          : null;
    if (!date || isNaN(date.getTime())) return { month: '', day: '' };
    return {
      month: formatDate(date, 'MMM'),
      day: formatDate(date, 'd')
    };
  }, []);

  const appendFormat = useCallback((url: string, format: 'jpg' | 'png') => {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}format=${format}`;
  }, []);

  const getPdfImageSrc = useCallback(
    (item: CreditPlanResultItem) => {
      const thumbnail = getThumbnailImageUrl(item.variationPk);
      const thumbnailForPdf = thumbnail ? appendFormat(thumbnail, 'jpg') : '';
      const fallback = getFullImageUrl(item.imageUrl);
      return thumbnailForPdf || fallback || undefined;
    },
    [appendFormat]
  );

  const topSectionElement = useMemo(
    () => (
      <View style={styles.topSection} wrap={false}>
        <View style={styles.documentTitle}>
          <View style={{ width: 37, height: 16 }}>
            <Svg viewBox="0 0 81 40">
              <Path
                fill="#EA0029"
                fillRule="evenodd"
                d="M12.3607 27.7037V39.9983H0V0.00993645C0 0.00993645 5.25329 -0.0146178 7.83187 0.00993645C9.58469 0.0265749 11.339 0.0225579 13.0934 0.0185408C17.2569 0.00900743 21.4208 -0.000526905 25.566 0.266001C34.761 0.851796 37.6108 8.41802 36.8966 15.1845C36.076 23.112 31.6193 27.3634 23.8184 27.6756C20.0827 27.8299 12.3607 27.7037 12.3607 27.7037ZM12.45 19.0079C12.45 19.0079 17.5693 19.0851 20.0689 18.9869C22.8535 18.8746 24.457 17.0471 24.5566 14.0971C24.6561 11.1471 23.269 9.11607 20.4672 8.87403C17.844 8.64954 12.45 8.82843 12.45 8.82843V19.0079ZM66.3216 30.052V40H38.9805V0.0185547H51.2141V30.052H66.3216ZM68.8516 0.0185547H80.9994V40H68.8516V0.0185547Z"
              />
            </Svg>
          </View>
          <Text style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
            {dateDisplay}
          </Text>
        </View>
        {/* Heading */}
        <View style={styles.heading}>
          <View style={styles.aiText}>
            {/* AI Text */}
            <View style={{ width: 17, height: 17, marginRight: 4 }}>
              <Svg>
                <Path
                  fill="#53565A"
                  d="M9.69075 1.69066C13.1752 2.62448 15.2434 6.20649 14.3097 9.69098C13.376 13.1753 9.79384 15.2434 6.30942 14.31C2.8249 13.3763 0.756886 9.79417 1.69044 6.30965C2.62413 2.82506 6.20616 0.756973 9.69075 1.69066ZM9.4749 2.49623C6.43515 1.68173 3.3105 3.48575 2.49601 6.5255C1.68165 9.56518 3.4856 12.6899 6.52527 13.5044C9.56485 14.3187 12.6896 12.5146 13.5042 9.47512C14.3186 6.43548 12.5145 3.31085 9.4749 2.49623ZM9.15018 3.70433C9.14899 3.7228 8.96977 6.5346 9.36143 7.21354C9.75167 7.88945 12.2585 9.13308 12.2949 9.1511C12.2518 9.14834 9.46191 8.97152 8.78591 9.3614C8.10506 9.75449 6.8481 12.2958 6.8481 12.2958C6.8481 12.2958 7.03114 9.46674 6.63805 8.78589C6.24461 8.10499 3.70341 6.84902 3.70341 6.84902C3.70341 6.84902 6.53272 7.03111 7.21357 6.63802C7.89064 6.24671 9.13617 3.73262 9.15018 3.70433Z"
                />
              </Svg>
            </View>
            <Text style={{ fontSize: 12, fontWeight: 500 }}>{aiPoweredText}</Text>
            {!!betaIcon && (
              <Svg viewBox="0 0 46 24" style={{ width: 40, height: 40, marginLeft: 4 }}>
                <Path
                  fill="none"
                  stroke="#53565A"
                  d="M4 0.5H42C43.933 0.5 45.5 2.067 45.5 4V20C45.5 21.933 43.933 23.5 42 23.5H4C2.067 23.5 0.5 21.933 0.5 20V4C0.5 2.067 2.067 0.5 4 0.5Z"
                />
                <Path
                  fill="#53565A"
                  d="M12.521 11.52h2.448c.36 0 .66-.1.9-.3.24-.208.36-.504.36-.888 0-.432-.108-.736-.324-.912-.216-.176-.528-.264-.936-.264h-2.448v2.364Zm-1.5-3.588h4.164c.768 0 1.384.176 1.848.528.464.352.696.884.696 1.596 0 .432-.108.804-.324 1.116-.208.304-.508.54-.9.708v.024c.528.112.928.364 1.2.756.272.384.408.868.408 1.452 0 .336-.06.652-.18.948-.12.288-.308.54-.564.756-.256.208-.584.376-.984.504-.4.12-.876.18-1.428.18h-3.936V7.932Zm1.5 7.344h2.652c.456 0 .808-.116 1.056-.348.256-.24.384-.576.384-1.008 0-.424-.128-.748-.384-.972-.248-.232-.6-.348-1.056-.348h-2.652v2.676Zm11.018-2.424a2.085 2.085 0 0 0-.144-.624 1.463 1.463 0 0 0-.312-.516 1.356 1.356 0 0 0-.48-.36 1.329 1.329 0 0 0-.624-.144c-.24 0-.46.044-.66.132-.192.08-.36.196-.504.348a1.798 1.798 0 0 0-.336.516c-.08.2-.124.416-.132.648h3.192Zm-3.192.9c0 .24.032.472.096.696.072.224.176.42.312.588.136.168.308.304.516.408.208.096.456.144.744.144.4 0 .72-.084.96-.252.248-.176.432-.436.552-.78h1.296a2.58 2.58 0 0 1-.372.9 2.68 2.68 0 0 1-.636.672 2.709 2.709 0 0 1-.84.396 3.17 3.17 0 0 1-.96.144c-.488 0-.92-.08-1.296-.24a2.738 2.738 0 0 1-.96-.672 2.973 2.973 0 0 1-.588-1.032c-.128-.4-.192-.84-.192-1.32 0-.44.068-.856.204-1.248.144-.4.344-.748.6-1.044.264-.304.58-.544.948-.72a2.857 2.857 0 0 1 1.248-.264c.488 0 .924.104 1.308.312.392.2.716.468.972.804s.44.724.552 1.164c.12.432.152.88.096 1.344h-4.56Zm4.976-3.456h1.032v-1.86h1.368v1.86h1.236v1.02h-1.236v3.312c0 .144.004.268.012.372a.788.788 0 0 0 .084.264.473.473 0 0 0 .204.168c.088.032.208.048.36.048h.288c.096-.008.192-.024.288-.048v1.056l-.444.048a4.019 4.019 0 0 1-.444.024c-.36 0-.652-.032-.876-.096a1.295 1.295 0 0 1-.516-.3 1.154 1.154 0 0 1-.252-.504c-.04-.2-.064-.428-.072-.684v-3.66h-1.032v-1.02Zm9.816 4.824c0 .168.02.288.06.36.048.072.136.108.264.108h.144c.056 0 .12-.008.192-.024v.948a1.66 1.66 0 0 1-.192.048 1.897 1.897 0 0 1-.228.06 2.483 2.483 0 0 1-.444.048c-.28 0-.512-.056-.696-.168-.184-.112-.304-.308-.36-.588a2.417 2.417 0 0 1-1.008.576c-.392.12-.772.18-1.14.18-.28 0-.548-.04-.804-.12a2.16 2.16 0 0 1-.684-.324 1.785 1.785 0 0 1-.468-.564 1.828 1.828 0 0 1-.168-.804c0-.384.068-.696.204-.936.144-.24.328-.428.552-.564a2.46 2.46 0 0 1 .768-.288c.288-.064.576-.112.864-.144.248-.048.484-.08.708-.096.224-.024.42-.06.588-.108a.9.9 0 0 0 .408-.216c.104-.104.156-.256.156-.456a.678.678 0 0 0-.132-.432.741.741 0 0 0-.312-.252 1.104 1.104 0 0 0-.408-.12 2.775 2.775 0 0 0-.432-.036c-.384 0-.7.08-.948.24-.248.16-.388.408-.42.744h-1.368c.024-.4.12-.732.288-.996.168-.264.38-.476.636-.636.264-.16.56-.272.888-.336a5.251 5.251 0 0 1 1.008-.096c.304 0 .604.032.9.096.296.064.56.168.792.312.24.144.432.332.576.564.144.224.216.5.216.828v3.192Zm-1.368-1.728a1.71 1.71 0 0 1-.768.252 8.896 8.896 0 0 0-.912.12c-.144.024-.284.06-.42.108-.136.04-.256.1-.36.18a.78.78 0 0 0-.252.3c-.056.12-.084.268-.084.444 0 .152.044.28.132.384.088.104.192.188.312.252.128.056.264.096.408.12.152.024.288.036.408.036.152 0 .316-.02.492-.06.176-.04.34-.108.492-.204.16-.096.292-.216.396-.36a.954.954 0 0 0 .156-.552v-1.02Z"
                />
              </Svg>
            )}
          </View>
          {/* Credit Plan Heading Text */}
          <View style={styles.headingText}>
            {heading && (
              <Svg height={46} viewBox="0 6 400 46" preserveAspectRatio="xMinYMin meet">
                <Defs>
                  <LinearGradient
                    id="grad"
                    x1="0"
                    y1="0"
                    x2="400"
                    y2="0"
                    gradientUnits="userSpaceOnUse"
                  >
                    <Stop offset="0" stopColor="#FFC600" />
                    <Stop offset="0.2115" stopColor="#EA0029" />
                    <Stop offset="0.5" stopColor="#9B2C98" />
                    <Stop offset="0.8269" stopColor="#040CDC" />
                  </LinearGradient>
                </Defs>
                <Text
                  x="0"
                  y="42"
                  fill="url(#grad)"
                  style={{
                    fontWeight: 700,
                    fontSize: 50,
                    letterSpacing: -1
                  }}
                >
                  {heading}
                </Text>
              </Svg>
            )}
            {jurisdictionName && <Text style={styles.jurisdictionName}>{jurisdictionName}</Text>}
            {subheading && (
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: 400,
                  color: '#53565A',
                  marginTop: 16
                }}
              >
                {templateString(
                  subheading,
                  {
                    License: jurisdictionName
                  },
                  true
                )}
              </Text>
            )}
          </View>
        </View>
      </View>
    ),
    [
      aiPoweredText,
      betaIcon,
      dateDisplay,
      heading,
      jurisdictionName,
      styles.aiText,
      styles.documentTitle,
      styles.heading,
      styles.headingText,
      styles.jurisdictionName,
      styles.topSection,
      subheading
    ]
  );

  const creditOverview = useMemo(() => {
    return (
      <View
        style={{
          ...styles.section,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        wrap={false}
      >
        <Text
          style={{
            color: '#000',
            fontWeight: 500,
            fontSize: 22
          }}
        >
          {total} planned credits
        </Text>
        {!!openJurisdictionBrowseUrl && (
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <Link
              href={openJurisdictionBrowseUrl}
              style={{
                color: '#000',
                textDecoration: 'none',
                fontSize: 16,
                fontWeight: 700,
                marginRight: 4
              }}
            >
              See requirements
            </Link>
            <Svg viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
              <Path
                d="M6.58594 5.00003L13.5859 12L6.58594 19L8.00015 20.4142L16.4144 12L8.00015 3.58582L6.58594 5.00003Z"
                fillRule="evenodd"
                fill="currentColor"
              />
            </Svg>
          </View>
        )}
      </View>
    );
  }, [openJurisdictionBrowseUrl, styles.section, total]);

  const onDemandPrograms = useMemo(() => {
    return (
      !!onDemand?.searchResultItems?.length && (
        <View style={styles.section}>
          <Text
            style={{
              color: '#000',
              fontWeight: 500,
              fontSize: 22,
              marginBottom: 20
            }}
          >
            Approved {onDemand.heading}
          </Text>
          {onDemand.searchResultItems.map((item, i) => {
            const isLast = i == (onDemand.searchResultItems?.length ?? 0) - 1;
            const formatsLabel = new Intl.ListFormat('en-us', {
              style: 'long',
              type: 'disjunction'
            }).format(item.formats ?? []);

            return (
              <View
                key={i}
                wrap={false}
                style={{
                  borderBottom: !isLast ? '1px solid #ECECEC' : 'unset'
                }}
              >
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginVertical: 26
                  }}
                >
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      flexBasis: '70%'
                    }}
                  >
                    <Link
                      href={item.url ?? ''}
                      style={{
                        marginBottom: 14,
                        color: '#000',
                        textDecoration: 'none',
                        fontSize: 16
                      }}
                    >
                      {item.title}
                    </Link>
                    <Text
                      style={{
                        fontSize: 14,
                        paddingTop: 1.5
                      }}
                    >
                      {formatDotSeparatedContent([
                        formatsLabel ?? 'On-Demand',
                        item.datesFormatted,
                        creditDetailsFormattedToString(item.creditDetailsFormatted ?? [])
                      ])}
                    </Text>
                  </View>
                  <View
                    style={{
                      width: 73,
                      height: 73,
                      borderRadius: 15,
                      overflow: 'hidden',
                      flexBasis: 'auto'
                    }}
                  >
                    <Image
                      src={getPdfImageSrc(item)}
                      cache={true}
                      style={{
                        width: 73,
                        height: 73,
                        objectFit: 'cover',
                        backgroundColor: '#ECECEC'
                      }}
                    />
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      )
    );
  }, [
    creditDetailsFormattedToString,
    formatDotSeparatedContent,
    getPdfImageSrc,
    onDemand?.heading,
    onDemand?.searchResultItems,
    styles.section
  ]);

  const livePrograms = useMemo(() => {
    return (
      !!live?.searchResultItems?.length && (
        <View style={styles.section}>
          <Text
            style={{
              color: '#000',
              fontWeight: 500,
              fontSize: 22,
              marginBottom: 20
            }}
          >
            Approved {live.heading}
          </Text>
          {live.searchResultItems.map((item, i) => {
            const locationLabel =
              item.locations
                ?.map(l => l.name)
                .filter(Boolean)
                .join(' & ') ||
              item.formats?.filter(Boolean).join(' & ') ||
              '';
            const { month, day } = getLiveProgramDateParts(item);
            return (
              <View
                key={i}
                wrap={false}
                style={{
                  ...styles.liveProgramCard,
                  marginBottom: i < (live.searchResultItems?.length ?? 0) - 1 ? 16 : 0
                }}
              >
                <View style={styles.liveProgramTextContainer}>
                  <View>
                    {locationLabel ? (
                      <Text
                        style={{
                          color: '#FFF',
                          fontSize: 12,
                          marginBottom: 8
                        }}
                      >
                        {locationLabel}
                      </Text>
                    ) : null}
                    <Text
                      style={{
                        color: '#FFF',
                        fontSize: 22,
                        marginBottom: 16
                      }}
                      wrap={false}
                    >
                      {item.title ?? ''}
                    </Text>
                  </View>
                  {item.url ? (
                    <Link href={item.url} style={styles.liveProgramRegisterButton}>
                      Register
                    </Link>
                  ) : null}
                </View>
                {month || day ? (
                  <View style={styles.liveProgramDate}>
                    {month ? (
                      <Text
                        style={{
                          marginBottom: 2
                        }}
                      >
                        {month}
                      </Text>
                    ) : null}
                    {day ? <Text>{day}</Text> : null}
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      )
    );
  }, [
    live?.searchResultItems,
    live?.heading,
    styles.section,
    styles.liveProgramCard,
    styles.liveProgramTextContainer,
    styles.liveProgramRegisterButton,
    styles.liveProgramDate,
    getLiveProgramDateParts
  ]);

  const disclaimerElement = useMemo(
    () => (
      <Text style={styles.disclaimerElement} wrap={false}>
        {extractTextFromHTML(disclaimer?.copy ?? '')}
      </Text>
    ),
    [disclaimer?.copy, styles.disclaimerElement]
  );

  const pmMessageElement = useMemo(
    () =>
      !!pmMessage && (
        <View
          style={{
            ...styles.section,
            color: '#000'
          }}
          wrap={false}
        >
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginBottom: 16,
              width: '100%',
              height: 26
            }}
          >
            <Svg viewBox="0 0 24 24" style={{ marginRight: 4, width: 24, height: 24 }}>
              <Path
                d="M20.4144 7.00003L9.00015 18.4142L3.58594 13L5.00015 11.5858L9.00015 15.5858L19.0002 5.58582L20.4144 7.00003Z"
                fillRule="evenodd"
                fill="currentColor"
              />
            </Svg>
            {pmMessage.heading && (
              <Text
                style={{
                  fontSize: 22,
                  fontWeight: 500
                }}
              >
                {pmMessage.heading}
              </Text>
            )}
          </View>
          {pmMessage.subHeading && (
            <Text
              style={{
                fontSize: 16,
                fontWeight: 400,
                maxWidth: '80%',
                width: 'auto'
              }}
            >
              {extractTextFromHTML(pmMessage.subHeading)}
            </Text>
          )}
          {pmMessage.text && (
            <Text
              style={{
                fontSize: 16,
                fontWeight: 400,
                maxWidth: '80%',
                width: 'auto'
              }}
            >
              {extractTextFromHTML(pmMessage.text)}
            </Text>
          )}
        </View>
      ),
    [pmMessage, styles.section]
  );

  return (
    <Document author="PLI" creator="PLI" title={fileName} creationDate={new Date()}>
      <Page size="A4" style={styles.page} dpi={300}>
        {/* Top Section, heading, subheading, etc */}
        {topSectionElement}

        {/* Credit Overview */}
        {creditOverview}

        {/* On-Demand Program Groupings */}
        {onDemandPrograms}

        {/* Live Programs */}
        {livePrograms}

        {/* PM Message */}
        {pmMessageElement}

        {/* Disclaimer */}
        {disclaimerElement}
      </Page>
    </Document>
  );
};

export default CreditPlanPdf;
